import { Inject, Injectable, Optional } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, concatAll, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { ConfirmationInstanceToken, ConfirmationInterface } from '../../core/interfaces/confirmation.interface';
import { XmlParserService } from '../../services/xml-parser.service';
import { XmlSerializerService } from '../../services/xml-serializer.service';
import { FormsConstants } from '../../types/forms-constants';
import { Signature } from '../../types/signature';
import {
    generateChecklistCopies,
    generateChecklistCopiesSuccess,
    removeParticipant,
    removeParticipantFromConfirmationSuccess,
    saveParticipantParticulars,
    saveParticipantParticularsSuccess,
    selectNextParticipant,
    selectParticipant,
    selectParticipantSuccess,
    selectPreviousParticipant,
    submitFormSuccess,
    updateConfirmationAttendeeSubmittedFieldsSuccess,
} from '../actions/form.actions';
import {
    getFormDoctype,
    getFormEmployeeSignatureFromMaster,
    getFormNotification,
    getFormParticipantCopies,
    getFormSelectedParticipantCopy,
    getFormType,
} from '../selectors/form.selector';
import { FormsState } from '../state/pace-forms.state';

// Effects triggered by the participant workflow
@Injectable()
export class FormParticipantsEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store<FormsState>,
        private readonly xmlParserService: XmlParserService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Optional() @Inject(ConfirmationInstanceToken) private readonly confirmationService: ConfirmationInterface,
        private readonly xmlSerializerService: XmlSerializerService,
    ) {}

    // update the attendees in the confirmation for PACE US - MARS app
    public updateConfirmationAttendeeSubmittedFields$ = createEffect(() =>
        this.actions$.pipe(
            ofType(submitFormSuccess),
            withLatestFrom(this.store.select(getFormNotification), this.store.select(getFormParticipantCopies)),
            mergeMap(([action, notification, participantCopies]) =>
                action.ids
                    .filter((id) => id !== FormsConstants.MASTER_PARTICIPANT_ID)
                    .map((id) => this.confirmationService.saveAttendee(notification, participantCopies[id])),
            ),
            concatAll(),
            map(() => updateConfirmationAttendeeSubmittedFieldsSuccess()),
            catchError((error) => {
                this.log.error(
                    `FormEffects: updateConfirmationAttendeeSubmittedFields$: error in pipeline: ${JSON.stringify(
                        error,
                    )}`,
                );
                return EMPTY;
            }),
        ),
    );

    public selectParticipantCopy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(selectParticipant, selectNextParticipant, selectPreviousParticipant),
            withLatestFrom(this.store.select(getFormSelectedParticipantCopy)),
            map(([, participantCopy]) => {
                this.xmlParserService.setXml(participantCopy.xml);
                return selectParticipantSuccess();
            }),
            catchError((error) => {
                this.log.error(`FormEffects: selectParticipantCopy$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public removeParticipant$ = createEffect(() =>
        // @teo can we get rid of that?
        this.actions$.pipe(
            ofType(removeParticipant),
            withLatestFrom(this.store.select(getFormNotification)),
            mergeMap(([action, notification]) =>
                this.confirmationService.removeAttendee(notification, action.participant),
            ),
            map((participant) =>
                removeParticipantFromConfirmationSuccess({
                    participant: participant,
                }),
            ),
            catchError((error) => {
                this.log.error(`FormEffects: removeParticipant$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public saveParticipant$ = createEffect(() =>
        this.actions$.pipe(
            ofType(saveParticipantParticulars),
            withLatestFrom(this.store.select(getFormNotification), this.store.select(getFormParticipantCopies)),
            mergeMap(([action, notification, participantCopies]) => {
                const participantCopy = participantCopies[action.participant.id];
                return this.confirmationService.saveAttendee(notification, participantCopy);
            }),
            map(() => saveParticipantParticularsSuccess()),
            catchError((error) => {
                this.log.error(`FormEffects: saveParticipant$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public generateChecklistCopies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(generateChecklistCopies),
            withLatestFrom(
                this.store.select(getFormParticipantCopies),
                this.store.select(getFormEmployeeSignatureFromMaster),
                this.store.select(getFormDoctype),
                this.store.select(getFormType),
            ),
            map(([action, participantCopies, employeeSignatureFromMaster, doctype, formType]) => {
                if (typeof employeeSignatureFromMaster === 'undefined') {
                    throw new Error(
                        `FormEffectsService: generateChecklistCopies$: Could not find employee signature in master copy!`,
                    );
                }
                // Trigger update of XML date fields for employee signature fields
                const currentXml = this.xmlParserService.getXml(doctype);
                action.selectedParticipants.forEach((p) => {
                    const participantCopy = participantCopies[p];
                    const employeeSignature = participantCopy.functionalElements[
                        employeeSignatureFromMaster.id
                    ] as Signature;
                    this.xmlParserService.setXml(participantCopy.xml);
                    this.xmlSerializerService.updateInputField(
                        employeeSignature.nameXmlId,
                        `${action.employeeFirstName} ${action.employeeLastName}`,
                        formType,
                    );
                    this.xmlSerializerService.updateInputField(
                        employeeSignature.dateXmlId,
                        action.employeeDate,
                        formType,
                    );
                });
                this.xmlParserService.setXml(currentXml);
                return generateChecklistCopiesSuccess();
            }),
            catchError((error) => {
                this.log.error(`FormEffects: abortFormSuccess$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );
}
